import { useEffect, useState } from 'react'

/**
 * Whether `node`'s content has outgrown its clamped height.
 *
 * Callers use this to make a card a scroll container only once it needs to be:
 * a permanently-scrollable card swallows wheel events over content that already
 * fits, so the chat transcript behind it can't be scrolled while the pointer is
 * over the card.
 */
export function useClampedOverflow(node: HTMLElement | null, enabled: boolean): boolean {
  const [overflows, setOverflows] = useState(false)

  useEffect(() => {
    if (!enabled || !node) {
      setOverflows(false)
      return
    }

    const measure = () => setOverflows(node.scrollHeight - node.clientHeight > 1)

    // Children resize as streamed content wraps; the clamped node itself doesn't.
    const resizes = new ResizeObserver(measure)
    const observeChildren = () => {
      resizes.disconnect()
      resizes.observe(node)
      for (const child of Array.from(node.children)) resizes.observe(child)
    }

    const mutations = new MutationObserver(() => {
      observeChildren()
      measure()
    })
    mutations.observe(node, { childList: true })

    observeChildren()
    measure()

    return () => {
      resizes.disconnect()
      mutations.disconnect()
    }
  }, [enabled, node])

  return overflows
}
